/**
 * Combined user + group autocomplete used by ACL / sharing dialogs: a thin
 * wrapper over `@brew/ui/actors`' `ActorPicker` (jorigin/brewauth#29 GB0)
 * that keeps this component's props, so its consumers (`AclMatrixEditor`,
 * `AclInputEditor`, `RoleAssignmentsPanel`, `UploadResourceModal` and the
 * Groups workspace's `AccessSection`) share one picker.
 *
 * Sources, queried concurrently and merged in this order:
 *  - people: Java's `GET /api/profiles/search` (`searchProfiles`), scoped by
 *    `contextId`; an empty query is sent as `*`, as before;
 *  - groups: brewauth's `GET /groups?visibility=acl` (`brewauthGroups`), the
 *    groups the caller can READ, as `AclActor`s through `toAclActor`.
 *
 * `onPick` still receives gui-react's `AclActor` (Java's shape): the picker
 * speaks the neutral `Actor`, so each source records the raw row by ref
 * (`rawByRef`) and the pick is looked up there.
 *
 * `$isSystem` rows (SYSTEM profiles) are still dropped, through `filter`.
 * The popup is portalled so it escapes scroll-clipped `.brew-modal` boxes,
 * as the old dropdown did: to `document.body` by default (`portal`), or,
 * inside a dialog that passes one, to that dialog's own container
 * (`@brew/ui` 0.19.0), so the popup is part of the dialog and not a separate
 * child of the body.
 */
import { useId, useMemo, useState } from 'react';
import {
  ActorPicker,
  type Actor,
  type ActorPickerProps,
  type ActorSource,
} from '@brew/ui/actors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import type { AclActor } from '@/api/acl';
import { brewauthGroups, toAclActor } from '@/api/brewauthGroups';
import { searchProfiles } from '@/api/profiles';
import { aclActorToActor, renderApiImage } from './aclActor';
import './AclEditor.css';
import { compareCodeUnits } from '@/utils/compare';

interface Props {
  /** Search users when true (default true). */
  includeUsers?: boolean;
  /** Search groups when true (default true). */
  includeGroups?: boolean;
  /** Optional namespace scope (legacy `contextId` for `?id=...` filter). */
  contextId?: string;
  /** Refs already in use — filtered out of the suggestions list. */
  excludeRefs: Set<string>;
  /** Called when the user picks an actor. */
  onPick: (actor: AclActor) => void;
  /** Override the field placeholder. */
  placeholder?: string;
  /** Override the small label text above the input. */
  label?: string;
  /**
   * Where the popup is portalled: `true` (the default) is `document.body`;
   * a dialog passes a getter for its own container (`PickerPortal`).
   */
  portal?: PickerPortal;
  /** The field takes no input and offers nothing (`ActorPicker`'s `disabled`). */
  disabled?: boolean;
}

/**
 * `ActorPicker`'s `portal` (`@brew/ui` 0.19.0): `true` for `document.body`,
 * or the element (or a getter for it) the popup is portalled into. A dialog
 * passes its OVERLAY, the fixed full-viewport root it renders into the body,
 * never its `.brew-modal` box: that box has a `backdrop-filter` and its own
 * overflow, so as the popup's containing block it would clip the popup to
 * the box and scroll it with the box's content.
 */
export type PickerPortal = ActorPickerProps['portal'];

/** How many rows each source asks for, as before. */
const SOURCE_LIMIT = 25;

/**
 * The input's look: the role-card search field (about 38px tall, a 10px
 * radius, a leading magnifier). The picker's own utilities already give the
 * height, radius, border and focus ring; these two restore the old field's
 * exact background and text colour.
 */
export const ACTOR_SEARCH_INPUT_CLASS = 'bg-[rgba(4,10,22,0.65)] text-[#e5f3fa]';

const notSystem = (a: Actor) => !a.isSystem;

function byLabel(a: Actor, b: Actor): number {
  const al = a.label.toLowerCase();
  const bl = b.label.toLowerCase();
  return compareCodeUnits(al, bl);
}

export default function ActorSearch({
  includeUsers = true,
  includeGroups = true,
  contextId,
  excludeRefs,
  onPick,
  placeholder = 'Type a name to search…',
  label = 'Add user or group',
  portal = true,
  disabled = false,
}: Readonly<Props>) {
  const id = useId();
  // The raw row behind each offered ref, filled by the sources and read on a
  // pick. One Map for the component's life (never replaced, so no re-render).
  const [rawByRef] = useState(() => new Map<string, AclActor>());

  const sources = useMemo<ActorSource[]>(() => {
    const keep = (rows: AclActor[]): Actor[] =>
      rows
        .filter((a) => a && typeof a.$reference === 'string' && a.$reference)
        .map((a) => {
          rawByRef.set(a.$reference, a);
          return aclActorToActor(a);
        });
    const out: ActorSource[] = [];
    if (includeUsers) {
      out.push(async (q) => {
        const res = await searchProfiles({
          text: q === '' ? '*' : q,
          sort: 'metadata.label.value',
          skip: 0,
          limit: SOURCE_LIMIT,
          ...(contextId ? { id: contextId } : {}),
        });
        return keep((res.data || []) as unknown as AclActor[]);
      });
    }
    if (includeGroups) {
      out.push(async (q) => {
        const page = await brewauthGroups.listGroups({ q, visibility: 'acl', limit: SOURCE_LIMIT });
        return keep(page.items.map(toAclActor));
      });
    }
    return out;
  }, [includeUsers, includeGroups, contextId, rawByRef]);

  return (
    <div className="acl-search">
      {label && (
        <label htmlFor={id} className="acl-search-label">
          {label}
        </label>
      )}
      <ActorPicker
        id={id}
        aria-label={label || placeholder}
        placeholder={placeholder}
        sources={sources}
        exclude={excludeRefs}
        filter={notSystem}
        sort={byLabel}
        minChars={0}
        portal={portal}
        disabled={disabled}
        renderImage={renderApiImage}
        inputClassName={ACTOR_SEARCH_INPUT_CLASS}
        leadingIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        onSelect={(a) => {
          const raw = rawByRef.get(a.ref);
          if (raw) onPick(raw);
        }}
      />
    </div>
  );
}
